import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
const WS_BASE = BACKEND_URL.replace(/^http/, "ws");

export const api = axios.create({ baseURL: API });

// --- App lock (require sign-in on every reopen) ---
// ON (default): token lives ONLY in sessionStorage — closing the tab (web) or
// killing the app (mobile) requires a fresh sign-in (ideally FaceID/passkey).
// OFF: token also persists in localStorage — direct access, stays signed in.
export function getAppLock() {
  try { return localStorage.getItem("pmf_app_lock") !== "0"; } catch (e) { return true; }
}

// ---- Chat text size preference (small | medium | large) — per device ----
export function getFontSize() {
  try { return localStorage.getItem("osc_font_size") || "medium"; } catch (e) { return "medium"; }
}

export function applyFontSize(size) {
  const s = size || getFontSize();
  try {
    if (s === "medium") delete document.documentElement.dataset.font;
    else document.documentElement.dataset.font = s;
  } catch (e) { /* ignore */ }
}

export function setFontSize(size) {
  try { localStorage.setItem("osc_font_size", size); } catch (e) { /* ignore */ }
  applyFontSize(size);
}

// Apply the persisted preference as soon as the app boots
applyFontSize();

// --- Biometric app-lock (native FaceID direct unlock) ---
// When App Lock is ON inside the NATIVE app, the session token is kept under
// a separate key that is only promoted to the live session after a successful
// native Face ID check (LAContext — no passkey sheet, no extra tap).
function isNativeApp() {
  try {
    return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
  } catch (e) { return false; }
}

export function getBioToken() {
  try { return localStorage.getItem("pmf_token_bio"); } catch (e) { return null; }
}

export function setBioToken(token) {
  try {
    if (token) localStorage.setItem("pmf_token_bio", token);
    else localStorage.removeItem("pmf_token_bio");
  } catch (e) { /* ignore */ }
}

export function setAppLock(enabled) {
  try {
    localStorage.setItem("pmf_app_lock", enabled ? "1" : "0");
    // migrate the current token to the right storage without ending the session
    const token = sessionStorage.getItem("pmf_token") || localStorage.getItem("pmf_token");
    if (token) {
      sessionStorage.setItem("pmf_token", token);
      if (enabled) localStorage.removeItem("pmf_token");
      else localStorage.setItem("pmf_token", token);
      // native app: keep the biometric-gated copy in sync with the lock setting
      if (enabled && isNativeApp()) setBioToken(token);
      else setBioToken(null);
    }
  } catch (e) {}
}

// --- Auth token handling ---
export function setAuthToken(token) {
  if (token) {
    sessionStorage.setItem("pmf_token", token);
    try {
      if (getAppLock()) {
        localStorage.removeItem("pmf_token");
        // Native app + lock ON: store the biometric-gated copy so the next
        // app open unlocks with direct Face ID (no passkey sheet)
        if (isNativeApp()) setBioToken(token);
      } else {
        localStorage.setItem("pmf_token", token);
        setBioToken(null);
      }
    } catch (e) {}
  } else {
    sessionStorage.removeItem("pmf_token");
    try {
      localStorage.removeItem("pmf_token");
      setBioToken(null);
    } catch (e) {}
  }
}

export function getAuthToken() {
  const s = sessionStorage.getItem("pmf_token");
  if (s) return s;
  // App lock OFF: restore the persistent session (direct access)
  if (!getAppLock()) {
    try {
      const l = localStorage.getItem("pmf_token");
      if (l) { sessionStorage.setItem("pmf_token", l); return l; }
    } catch (e) {}
  }
  return null;
}

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Offline: a dropped request is expected, not a crash (screens fall back to the mirror)
if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (e) => {
    if (e.reason?.code === "ERR_NETWORK" || e.reason?.message === "Network Error") e.preventDefault();
  });
}

// Offline mirror: cached blobs (lib/offlineSync registers the lookup to avoid a cycle)
let cachedMediaLookup = () => null;
export function registerMediaCache(fn) { cachedMediaLookup = fn; }

export function mediaSrc(media) {
  if (!media) return "";
  if (media.url) return media.url;
  return cachedMediaLookup(media.id) || `${API}/media/${media.id}/raw`;
}

export function mediaSrcById(id) {
  return cachedMediaLookup(id) || `${API}/media/${id}/raw`;
}

export function openWS(channel, onMessage, onReconnect) {
  let ws;
  let closed = false;
  let pingInterval;
  let retryTimer;
  let pongTimer;
  let everConnected = false;

  const connect = () => {
    clearTimeout(retryTimer);
    ws = new WebSocket(`${WS_BASE}/api/ws/${channel}`);
    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.event === "pong") { clearTimeout(pongTimer); pongTimer = null; return; }
        onMessage(data);
      } catch (err) {
        /* ignore */
      }
    };
    ws.onopen = () => {
      // Refetch on EVERY open — including the FIRST connect: anything created
      // between the caller's initial REST fetch and this socket opening would
      // otherwise be lost until the next reconnect (e.g. an inline-reply
      // message posted while the app was still booting).
      if (onReconnect) {
        try { onReconnect(); } catch (e2) { /* ignore */ }
      }
      everConnected = true;
      pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send("ping");
      }, 25000);
    };
    ws.onclose = () => {
      clearInterval(pingInterval);
      clearTimeout(pongTimer);
      pongTimer = null;
      if (!closed) retryTimer = setTimeout(connect, 2000);
    };
    ws.onerror = () => {
      try { ws.close(); } catch (e2) { /* ignore */ }
    };
  };
  connect();

  // Back from background (phone): iOS silently kills idle sockets WITHOUT firing
  // onclose — the app would look "connected" but never receive anything again.
  // Probe the socket on every return to the foreground and rebuild it if dead.
  const onVisible = () => {
    if (document.hidden || closed) return;
    if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
      connect();
      return;
    }
    if (ws.readyState === WebSocket.OPEN && !pongTimer) {
      try { ws.send("ping"); } catch (e) { try { ws.close(); } catch (e2) { /* ignore */ } return; }
      pongTimer = setTimeout(() => {
        pongTimer = null;
        try { ws.close(); } catch (e) { /* ignore */ } // no pong → zombie → onclose → reconnect
      }, 4000);
    }
  };
  document.addEventListener("visibilitychange", onVisible);

  return () => {
    closed = true;
    clearInterval(pingInterval);
    clearTimeout(retryTimer);
    clearTimeout(pongTimer);
    document.removeEventListener("visibilitychange", onVisible);
    try { ws.close(); } catch (e) { /* ignore */ }
  };
}

/**
 * Send a chat message AND echo it locally right away.
 * The open thread renders the returned message instantly (deduped by id),
 * so the sender never depends on the WebSocket round-trip — which can be
 * down/reconnecting on mobile right after the app returns from background.
 */
export async function sendMessage(body) {
  const res = await api.post("/messages", body);
  try {
    window.dispatchEvent(new CustomEvent("osc-queued-sent", { detail: res.data }));
  } catch (e) { /* ignore */ }
  return res.data;
}

// Client-side image compression -> returns base64 (no data: prefix) + contentType
export async function compressImage(file, maxDim = 1600, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const scale = maxDim / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      const dataUrl = canvas.toDataURL("image/jpeg", quality);
      URL.revokeObjectURL(url);
      resolve({ base64: dataUrl.split(",")[1], contentType: "image/jpeg", dataUrl });
    };
    img.onerror = reject;
    img.src = url;
  });
}

export function dataUrlToBase64(dataUrl) {
  return dataUrl.split(",")[1];
}


const UPLOAD_CHUNK = 4 * 1024 * 1024; // < proxy body limit (100MB) by a wide margin

/** Chunked upload for big files (videos): 4MB parts → /media/upload-chunk, then /media/upload-complete. */
/** Reads [start,end) of a File/Blob OR of a native file ({ native: true, url }) without loading the whole file in memory. */
async function readPart(file, start, end) {
  if (!file.native) return file.slice(start, end);
  const resp = await fetch(file.url, { headers: { Range: `bytes=${start}-${end - 1}` } });
  if (resp.status === 206) return await resp.blob();
  if (!resp.ok) throw new Error(`read ${resp.status}`);
  // server ignored Range: fall back to slicing the full body (cached once)
  if (!file._full) file._full = await resp.blob();
  return file._full.slice(start, end);
}

export async function uploadFileChunked({ file, kind = "video", jobId = null, uploadedBy = null, category = null, filename = null, onProgress = null, signal = null }) {
  const uploadId = (crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const total = Math.max(1, Math.ceil(file.size / UPLOAD_CHUNK));
  const name = filename || file.name || "video.mp4";
  for (let i = 0; i < total; i++) {
    const part = await readPart(file, i * UPLOAD_CHUNK, Math.min(file.size, (i + 1) * UPLOAD_CHUNK));
    const fd = new FormData();
    fd.append("upload_id", uploadId);
    fd.append("index", String(i));
    fd.append("chunk", part, `${name}.part${i}`);
    let attempt = 0;
    for (;;) {
      try {
        await api.post("/media/upload-chunk", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          signal,
          timeout: 120000,
          onUploadProgress: (ev) => {
            if (onProgress && ev.total) onProgress(Math.min(0.97, (i + ev.loaded / ev.total) / total));
          },
        });
        break;
      } catch (err) {
        if (signal?.aborted || ++attempt > 3) throw err;
        await new Promise((r) => setTimeout(r, 800 * attempt));
      }
    }
  }
  const res = await api.post("/media/upload-complete", {
    upload_id: uploadId, total, filename: name, content_type: file.type || null,
    kind, job_id: jobId, uploaded_by: uploadedBy, category,
  }, { signal, timeout: 300000 });
  if (onProgress) onProgress(1);
  return res.data;
}

export async function uploadMedia({ base64, contentType = "image/jpeg", kind = "image", jobId = null, category = null, originalId = null, parentId = null, versionLabel = null, uploadedBy = null, filename = null, onProgress = null, textOverlays = null }) {
  const res = await api.post("/media", {
    data_base64: base64,
    content_type: contentType,
    kind,
    job_id: jobId,
    category,
    original_id: originalId,
    parent_id: parentId,
    version_label: versionLabel,
    uploaded_by: uploadedBy,
    filename,
    text_overlays: textOverlays,
  }, onProgress ? {
    onUploadProgress: (e) => {
      if (e.total) onProgress(Math.min(1, e.loaded / e.total));
    },
  } : undefined);
  return res.data;
}

export async function translateMessages(messageIds, lang) {
  let uid = null;
  try { uid = JSON.parse(localStorage.getItem("pmf_user") || "null")?.id || null; } catch (e) {}
  const res = await api.post("/messages/translate", { message_ids: messageIds, target_lang: lang, user_id: uid });
  return res.data;
}

export async function translateArtifacts(issueIds, taskIds, lang) {
  const res = await api.post("/artifacts/translate", { issue_ids: issueIds, task_ids: taskIds, target_lang: lang });
  return res.data;
}

export function timeAgo(iso, lang = "en") {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  const isPt = lang === "pt";
  if (mins < 1) return isPt ? "agora" : "now";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
}

export function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

export function fmtDate(iso, lang = "en") {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString(lang === "pt" ? "pt-BR" : "en-US", { month: "short", day: "numeric" });
}
